import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { upsertPluviometerDailyReading } from "../../utils/farmPluviometryApi";

function todayIsoDate() {
  const date = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function formatDayLabel(dateStr) {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function PluviometerDayEditModal({
  farmId,
  stationUuid,
  stationLabel,
  dateStr,
  initialMm,
  allowDateChange = false,
  replaceDays = [],
  onClose,
  onSaved,
}) {
  const { t } = useTranslation();
  const [dateValue, setDateValue] = useState(dateStr);
  const [rainMm, setRainMm] = useState(
    initialMm != null && initialMm !== "" ? String(initialMm) : ""
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const selectedDate = allowDateChange ? dateValue : dateStr;

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const parsed = rainMm === "" ? 0 : Number(rainMm);
      if (Number.isNaN(parsed) || parsed < 0) {
        setError(t("Informe um valor válido em milímetros."));
        setSaving(false);
        return;
      }

      if (!selectedDate) {
        setError(t("Informe a data da leitura."));
        setSaving(false);
        return;
      }

      if (selectedDate > todayIsoDate()) {
        setError(t("Não é permitido registrar chuva em data futura."));
        setSaving(false);
        return;
      }

      const result = await upsertPluviometerDailyReading(farmId, stationUuid, {
        date: selectedDate,
        rainMm: parsed,
      });
      const daysToClear = (replaceDays || []).filter(
        (day) => day && day !== selectedDate
      );
      if (daysToClear.length) {
        await Promise.all(
          daysToClear.map((day) =>
            upsertPluviometerDailyReading(farmId, stationUuid, {
              date: day,
              rainMm: 0,
            })
          )
        );
      }
      onSaved(result);
    } catch (submitError) {
      setError(t("Não foi possível salvar a leitura deste dia."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="farm-pluviometer-modal">
      <button
        type="button"
        className="farm-pluviometer-modal__backdrop"
        aria-label={t("Fechar")}
        onClick={onClose}
      />
      <form className="farm-pluviometer-modal__panel" onSubmit={handleSubmit}>
        <h3>
          {replaceDays && replaceDays.length
            ? t("Editar chuva do período")
            : t("Editar chuva")}
        </h3>
        <p className="farm-pluviometer-modal__hint">
          {stationLabel}
          {selectedDate ? ` — ${formatDayLabel(selectedDate)}` : ""}
        </p>
        {replaceDays && replaceDays.length ? (
          <p className="farm-pluviometer-modal__hint">
            {t(
              "O valor do período será substituído. Não soma em cima da chuva já lançada."
            )}
          </p>
        ) : null}
        {allowDateChange ? (
          <label>
            {t("Data")}
            <input
              type="date"
              value={dateValue}
              max={todayIsoDate()}
              onChange={(event) => setDateValue(event.target.value)}
              required
            />
          </label>
        ) : null}
        <label>
          {t("Chuva (mm)")}
          <input
            type="number"
            min="0"
            step="0.1"
            value={rainMm}
            onChange={(event) => setRainMm(event.target.value)}
            autoFocus
          />
        </label>
        {error ? <div className="farm-pluviometer-modal__error">{error}</div> : null}
        <div className="farm-pluviometer-modal__actions">
          <button type="button" className="btn btn-default" onClick={onClose}>
            {t("Cancelar")}
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? t("Salvando...") : t("Salvar")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PluviometerDayEditModal;
